import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Trash2, RefreshCw, Plus, Calendar, Users, Edit, Building2, Eye } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';

import { partnerApi } from '../lib/api/partnerApi';
import { PartnerDto } from '../lib/api/types';

interface PartnerListProps {
  onCreatePartner?: () => void;
  onEditPartner?: (partner: PartnerDto) => void;
  onViewPartner?: (partner: PartnerDto) => void;
}

export const PartnerList: React.FC<PartnerListProps> = ({ onCreatePartner, onEditPartner, onViewPartner }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Query to fetch all partners
  const {
    data: partnerData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['partners'],
    queryFn: partnerApi.getAllPartners,
  });

  // Delete partner mutation
  const deletePartnerMutation = useMutation({
    mutationFn: partnerApi.deletePartner,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      toast.success(`Partner ${data.partnerCode} deleted successfully`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete partner: ${error.message}`);
    },
  });

  // Clear all partners mutation
  const clearAllPartnersMutation = useMutation({
    mutationFn: partnerApi.clearAllPartners,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      toast.success(`${data.deletedCount} partners cleared successfully`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to clear partners: ${error.message}`);
    },
  });

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Loading Partners...
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-destructive">Error Loading Partners</CardTitle>
          <CardDescription>
            {error instanceof Error ? error.message : 'An unknown error occurred'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => refetch()} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const partners = partnerData?.partners || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Partners ({partners.length})
              </CardTitle>
              <CardDescription>
                Manage partner organizations and their configurations
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => refetch()} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              {onCreatePartner && (
                <Button onClick={onCreatePartner} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Partner
                </Button>
              )}
              {partners.length > 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" size="sm">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Clear All
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Clear All Partners</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete all {partners.length} partners. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => clearAllPartnersMutation.mutate()}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Clear All Partners
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Partners List */}
      {partners.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Partners Found</h3>
            <p className="text-muted-foreground text-center mb-4">
              Get started by creating your first partner organization.
            </p>
            {onCreatePartner && (
              <Button onClick={onCreatePartner}>
                <Plus className="h-4 w-4 mr-2" />
                Create Partner
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {partners.map((partner) => (
            <PartnerCard
              key={partner.partnerId}
              partner={partner}
              onView={onViewPartner}
              onEdit={onEditPartner}
              onDelete={(partnerId) => deletePartnerMutation.mutate(partnerId)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface PartnerCardProps {
  partner: PartnerDto;
  onView?: (partner: PartnerDto) => void;
  onEdit?: (partner: PartnerDto) => void;
  onDelete?: (partnerId: string) => void;
}

const PartnerCard: React.FC<PartnerCardProps> = ({ partner, onView, onEdit, onDelete }) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${
              partner.isActive ? 'bg-green-500' : 'bg-gray-400'
            }`} />
            <div>
              <CardTitle className="text-lg">{partner.partnerName}</CardTitle>
              <CardDescription className="flex items-center gap-4">
                <span className="font-mono text-sm">{partner.partnerCode}</span>
                {partner.billerId && (
                  <span className="text-xs bg-muted px-2 py-1 rounded">
                    Biller: {partner.billerId}
                  </span>
                )}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onView && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onView(partner)}
                title="View Details"
              >
                <Eye className="h-4 w-4" />
              </Button>
            )}
            {onEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(partner)}
                title="Edit Partner"
              >
                <Edit className="h-4 w-4" />
              </Button>
            )}
            {onDelete && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Partner</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete partner "{partner.partnerName}" ({partner.partnerCode})?
                      This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onDelete(partner.partnerId)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete Partner
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {partner.description && (
            <p className="text-sm text-muted-foreground">{partner.description}</p>
          )}
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              Created: {new Date(partner.createdAt).toLocaleDateString()}
            </div>
            {partner.updatedAt && (
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Updated: {new Date(partner.updatedAt).toLocaleDateString()}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              partner.isActive
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
            }`}>
              {partner.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
